/* The file maker, and the generator above it.

   A chat hands the application back as *text*, and turning text into a file is
   the step that stopped most of the room at the first workshop — not the
   prompting. So the file is made here, in the page: a Blob and a download, with
   nothing uploaded and nothing fetched.

   The generator is the other half. The brief goes out, the finished application
   comes back, and it lands in the same field a pasted chat answer would. It is
   the half that spends money, so it needs a signed-in account and a switch that
   is on; both are decided by the server and neither can be talked into being
   true from here.

   OWNED BY ITEM 7 OF docs/plan/00-prestavba-2026-08.md. */

import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import { useFeature } from "../lib/portal";
import { markdown } from "./markdown";
import { SANDBOX } from "./shared";
import type { MakerBlock } from "../lib/types";

/* A chat wraps a code block in fences, and a fence is not part of the file. */
function stripFence(raw: string): { code: string; stripped: boolean } {
  const text = raw.trim();
  const m = /^```[a-zA-Z]*\n([\s\S]*?)\n?```$/.exec(text);
  return m ? { code: m[1]!.trim(), stripped: true } : { code: text, stripped: false };
}

/* Without a charset every háček turns into a question mark, which reads as a
   broken application rather than a wrong save. */
function ensureCharset(code: string): { code: string; added: boolean } {
  if (/<meta[^>]+charset/i.test(code)) return { code, added: false };
  if (/<head[^>]*>/i.test(code)) {
    return { code: code.replace(/<head[^>]*>/i, (m) => `${m}\n<meta charset="utf-8">`), added: true };
  }
  return { code, added: false };
}

type Msg = { text: string; bad?: boolean } | null;

export function Maker({ block }: { block: MakerBlock }) {
  const { t, ui, href } = useI18n();
  const makerOn = useFeature("maker");
  const genOn = useFeature("gen");

  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [brief, setBrief] = useState("");
  const [name, setName] = useState(block.fileName ?? "aplikace.html");
  const [msg, setMsg] = useState<Msg>(null);
  const [running, setRunning] = useState(false);
  const [lines, setLines] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const previewClose = useRef<HTMLButtonElement>(null);

  /* While the run is on screen, Escape closes it — and only it. Captured and
     stopped the way the QR overlay holds its keys: in the deck Escape leaves
     the deck, and closing the preview must not also close the slide. The
     focus follows the overlay in, so the keyboard is one press from out. */
  useEffect(() => {
    if (preview === null) return;
    previewClose.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setPreview(null);
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [preview]);
  const abort = useRef<AbortController | null>(null);

  // A run that was abandoned must not speak over the one that replaced it.
  useEffect(() => () => abort.current?.abort(), []);

  const clean = useCallback(() => {
    const fenced = stripFence(code);
    const charset = ensureCharset(fenced.code);
    return { ...charset, stripped: fenced.stripped };
  }, [code]);

  function download() {
    const { code: out, stripped, added } = clean();
    if (!out) return setMsg({ text: ui("maker.empty"), bad: true });
    const url = URL.createObjectURL(new Blob([out], { type: "text/html;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name.endsWith(".html") ? name : `${name}.html`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 8000);
    // Both edits are announced. A file that was silently changed is a file
    // nobody can debug.
    const edits = [stripped && ui("maker.fenceRemoved"), added && ui("maker.charsetAdded")]
      .filter(Boolean).join(" ");
    setMsg({ text: `${ui("maker.saved")} ${edits}`.trim() });
  }

  function run() {
    const { code: out } = clean();
    if (!out) return setMsg({ text: ui("maker.empty"), bad: true });
    setPreview(out);
  }

  async function generate() {
    if (running) {
      // Put the button back at once rather than waiting for the stream to
      // report in: a hung connection never reports in.
      abort.current?.abort();
      setRunning(false);
      setMsg({ text: ui("gen.stopped") });
      return;
    }
    if (!brief.trim()) return setMsg({ text: ui("gen.emptyBrief"), bad: true });

    const controller = new AbortController();
    abort.current = controller;
    setRunning(true);
    setLines(0);
    setCode("");
    setNote("");
    setMsg({ text: ui("gen.starting") });

    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: brief }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const detail = await res.json().catch(() => ({}));
        setMsg({ text: detail.detail ?? ui("gen.failed"), bad: true });
        setRunning(false);
        return;
      }

      // One JSON object per line: c = code, n = note, i = status, err, done.
      // The two channels never mix — a line of the model's prose inside the
      // file is a broken file.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let count = 0;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n");
        buffer = parts.pop() ?? "";
        for (const line of parts) {
          if (!line.trim()) continue;
          const obj = JSON.parse(line);
          if (obj.c !== undefined) { setCode((c) => c + obj.c); count += 1; setLines(count); }
          else if (obj.n !== undefined) setNote((n) => n + obj.n);
          else if (obj.i) setMsg({ text: obj.i });
          else if (obj.err) setMsg({ text: obj.err, bad: true });
          else if (obj.done) setMsg({ text: ui("gen.done") });
        }
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") setMsg({ text: ui("gen.failed"), bad: true });
    } finally {
      if (abort.current === controller) {
        setRunning(false);
        abort.current = null;
      }
    }
  }

  if (!makerOn) return null;

  return (
    <div className="maker">
      {block.title ? <h3>{t(block.title)}</h3> : null}
      {block.lead ? <div className="lead">{t(block.lead)}</div> : null}

      {genOn ? (
        <div className="gen">
          <label className="gen-label" htmlFor="gen-brief">
            {t(block.genLabel) || ui("gen.label")}
          </label>
          <textarea id="gen-brief" className="gen-q" rows={3} spellCheck={false}
                    value={brief} onChange={(e) => setBrief(e.target.value)}
                    placeholder={t(block.genHint) || ui("gen.hint")} />
          <div className="gen-bar">
            <button type="button" className="maker-go" onClick={() => void generate()}>
              {running ? ui("gen.stop") : ui("gen.go")}
            </button>
            {/* No percentage: the length of the answer is not known in advance,
                and a bar that fills to ninety and stops teaches a room to
                distrust the page. The line count only goes up. */}
            <span className="gen-state">{running ? `${lines} ${ui("gen.lines")}` : ""}</span>
          </div>
          <div className="gen-prog" aria-hidden="true"
               style={{ visibility: running ? "visible" : "hidden" }}><i /></div>
          {/* Markdown, because that is what a model writes prose in. Shown
              as plain text it arrived with its own hashes and asterisks — see
              blocks/markdown.tsx. */}
          {note ? <div className="gen-said">{markdown(note, href)}</div> : null}
        </div>
      ) : null}

      <textarea className="maker-code" rows={genOn ? 7 : 9} spellCheck={false}
                value={code} onChange={(e) => setCode(e.target.value)}
                placeholder={ui("maker.paste")} />
      <div className="maker-bar">
        <label className="maker-name">
          {ui("maker.fileName")}
          <input type="text" value={name} spellCheck={false}
                 onChange={(e) => setName(e.target.value)} />
        </label>
        <button type="button" className="maker-go" onClick={download}>{ui("maker.download")}</button>
        <button type="button" className="maker-go" onClick={run}>{ui("maker.run")}</button>
      </div>
      {msg ? <div className={"maker-msg" + (msg.bad ? " bad" : "")}>{msg.text}</div> : null}

      {preview !== null ? (
        <div className="runov" role="dialog" aria-modal="true"
             aria-label={ui("maker.previewTitle")}>
          <button type="button" ref={previewClose} className="runclose"
                  onClick={() => setPreview(null)}>
            {ui("maker.close")}
          </button>
          {/* The same sandbox as the application of a lesson — see
              blocks/shared.tsx, which says why it is also the truer preview.
              This code came out of a chat or out of the model a moment ago, and
              unsandboxed it would run on the portal's origin with the session
              cookie of whoever is signed in within reach: one press of Spustit
              would be enough to flip a switch or spend the generator's ceiling.
              Closing empties the overlay, which is what stops whatever the
              preview started. */}
          <iframe title={ui("maker.previewTitle")} sandbox={SANDBOX} srcDoc={preview} />
        </div>
      ) : null}
    </div>
  );
}
